import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The interviewee journey shown on the Observatory Split brand panel
 * (LUM-155 storyboard). Pages report their current step via
 * useJourneyStep; the SplitLayout rail lights the active waypoint.
 */
export const JOURNEY_STEPS = [
  'Confirm details',
  'Ready to begin',
  'Your Dialogue',
  'Wrap up',
] as const;

export type JourneyStep = 0 | 1 | 2 | 3;

interface JourneyState {
  step: JourneyStep;
  setStep: (step: JourneyStep) => void;
}

const JourneyContext = createContext<JourneyState | null>(null);

export function JourneyProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [step, setStep] = useState<JourneyStep>(1);
  const value = useMemo(() => ({ step, setStep }), [step]);
  return (
    <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>
  );
}

export function useJourney(): JourneyState {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error('useJourney must be used inside JourneyProvider');
  return ctx;
}

/** Declare the journey step this view (or view state) represents. */
export function useJourneyStep(step: JourneyStep) {
  const { setStep } = useJourney();
  useEffect(() => {
    setStep(step);
  }, [step, setStep]);
}
